import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminGetArticle, adminCreateArticle, adminUpdateArticle, getCategories, adminUploadImage, adminUploadArticlePdf } from "@/lib/api";
import { useDraftAutosave, readDraft, clearDraft, timeAgo } from "@/lib/useDraft";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyArticle = {
  slug: "",
  title: "",
  category: "what-is-sibo",
  kicker: "",
  excerpt: "",
  author: "Editorial Desk",
  reviewer: "",
  reading_time: "5 min read",
  date: "",
  image: "",
  body: "",
  published: true,
};

export default function ArticleEditor() {
  const { slug } = useParams();
  const editing = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyArticle);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [inlineImgUploading, setInlineImgUploading] = useState(false);
  const bodyRef = useRef(null);
  const [draftMeta, setDraftMeta] = useState(null); // { savedAt } when a draft is active
  const [initialized, setInitialized] = useState(false);

  const draftKey = editing ? `sibo_draft_article_${slug}` : "sibo_draft_article_new";

  // Load draft (if any) then merge with server data.
  useEffect(() => {
    let cancelled = false;
    getCategories().then(setCats).catch(() => {});
    (async () => {
      const draft = readDraft(draftKey);
      if (editing) {
        try {
          const serverArticle = await adminGetArticle(slug);
          if (cancelled) return;
          // If a draft exists and is newer than the server updated_at, offer to use it
          if (draft && draft.value) {
            const serverTs = new Date(serverArticle.updated_at || serverArticle.created_at || 0).getTime();
            if (draft.savedAt > serverTs) {
              setForm(draft.value);
              setDraftMeta({ savedAt: draft.savedAt });
            } else {
              setForm(serverArticle);
              clearDraft(draftKey);
            }
          } else {
            setForm(serverArticle);
          }
        } catch (_) {
          if (!cancelled) toast.error("Could not load article");
        }
      } else if (draft && draft.value) {
        setForm(draft.value);
        setDraftMeta({ savedAt: draft.savedAt });
      }
      if (!cancelled) setInitialized(true);
    })();
    return () => { cancelled = true; };
  }, [slug, editing, draftKey]);

  // Autosave form to localStorage after user starts editing
  useDraftAutosave(draftKey, form, { enabled: initialized });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    setImgError(false);
    try {
      const res = await adminUploadImage(file);
      update("image", `${BACKEND}${res.url}`);
      toast.success("Image uploaded");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  // Insert a snippet into the body textarea at the cursor position
  const insertIntoBody = (snippet) => {
    const el = bodyRef.current;
    const current = form.body || "";
    if (!el) {
      update("body", current + (current.endsWith("\n") ? "" : "\n\n") + snippet + "\n");
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const needsLeadingBreak = start > 0 && current[start - 1] !== "\n";
    const insert = (needsLeadingBreak ? "\n\n" : "") + snippet + "\n";
    const next = current.slice(0, start) + insert + current.slice(end);
    update("body", next);
    // restore focus + move caret to after inserted text
    requestAnimationFrame(() => {
      if (bodyRef.current) {
        const pos = start + insert.length;
        bodyRef.current.focus();
        bodyRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handlePdfAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true);
    try {
      const res = await adminUploadArticlePdf(file);
      const url = `${BACKEND}${res.url}`;
      const label = res.original_filename || "Download PDF";
      insertIntoBody(`[Download: ${label}](${url})`);
      toast.success("PDF attached — link inserted into body");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "PDF upload failed");
    } finally {
      setPdfUploading(false);
      // reset the input so re-uploading the same file works
      e.target.value = "";
    }
  };

  const handleInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInlineImgUploading(true);
    try {
      const res = await adminUploadImage(file);
      const url = `${BACKEND}${res.url}`;
      const alt = (file.name || "image").replace(/\.[^.]+$/, "");
      insertIntoBody(`![${alt}](${url})`);
      toast.success("Image inserted into body");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Image upload failed");
    } finally {
      setInlineImgUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      if (editing) {
        await adminUpdateArticle(slug, form);
        clearDraft(draftKey);
        setDraftMeta(null);
        toast.success("Saved");
      } else {
        const created = await adminCreateArticle(form);
        clearDraft(draftKey);
        setDraftMeta(null);
        toast.success("Created");
        navigate(`/admin/articles/edit/${created.slug}`);
        return;
      }
    } catch (e) {
      // 401s are handled by the axios interceptor (session-expired toast + redirect)
      if (e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Save failed");
      }
    } finally { setLoading(false); }
  };

  const discardDraft = () => {
    clearDraft(draftKey);
    setDraftMeta(null);
    if (editing) {
      adminGetArticle(slug).then(setForm).catch(() => {});
    } else {
      setForm(emptyArticle);
    }
    toast.success("Draft discarded");
  };

  return (
    <div data-testid="article-editor">
      <Link to="/admin/articles" className="overline editorial-link">← All articles</Link>
      <div className="flex items-center justify-between flex-wrap gap-4 mt-4 mb-10">
        <h1 className="font-serif-editorial tight-track" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1 }}>
          {editing ? "Edit article" : "New article"}
        </h1>
        <div className="flex gap-4 flex-wrap items-center">
          {editing && form.slug && (
            <a
              href={`/article/${form.slug}?v=${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="overline editorial-link"
              data-testid="view-live-article"
            >
              View live →
            </a>
          )}
          <button onClick={() => setPreview((p) => !p)} className="btn-outline" data-testid="toggle-preview">
            {preview ? "Edit" : "Preview"}
          </button>
          <button onClick={save} disabled={loading} className="btn-olive" data-testid="save-article">
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {draftMeta && (
        <div
          className="mb-8 p-4 flex items-start justify-between gap-4 flex-wrap border"
          style={{ borderColor: "rgba(166,154,139,0.6)", background: "var(--sand)" }}
          data-testid="draft-banner"
        >
          <div className="text-sm" style={{ lineHeight: 1.6 }}>
            <strong className="font-serif-editorial">Unsaved draft restored</strong> — last edited {timeAgo(draftMeta.savedAt)}. Click <em>Save</em> to publish, or discard to start over.
          </div>
          <button
            onClick={discardDraft}
            className="overline editorial-link"
            style={{ color: "#8a3a3a" }}
            data-testid="discard-draft"
          >
            Discard draft
          </button>
        </div>
      )}

      {preview ? (
        <div className="border p-10" style={{ borderColor: "rgba(166,154,139,0.5)", backgroundColor: "var(--sand)" }} data-testid="article-preview">
          <div className="overline">{form.kicker} · {form.reading_time}</div>
          <h2 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "2.6rem", lineHeight: 1 }}>{form.title || "Untitled"}</h2>
          <p className="mt-6 text-lg font-serif-editorial" style={{ maxWidth: "42rem" }}>{form.excerpt}</p>
          <div className="mt-10 prose-editorial">
            <ReactMarkdown
              components={{
                h2: ({node, ...p}) => <h2 className="font-serif-editorial mt-10 mb-4" style={{ fontSize: "1.6rem" }} {...p} />,
                p: ({node, ...p}) => <p className="text-base mt-4" style={{ lineHeight: 1.7, maxWidth: "40rem" }} {...p} />,
                ul: ({node, ...p}) => <ul className="list-disc pl-6 mt-4 space-y-2" {...p} />,
                li: ({node, ...p}) => <li style={{ lineHeight: 1.6 }} {...p} />,
              }}
            >{form.body || "_Nothing to preview yet._"}</ReactMarkdown>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-5">
            <Field label="Title" testid="field-title">
              <input type="text" value={form.title} onChange={(e) => update("title", e.target.value)} className="editor-input" data-testid="input-title" />
            </Field>
            <Field label="Slug (auto if blank)" testid="field-slug">
              <input type="text" value={form.slug || ""} onChange={(e) => update("slug", e.target.value)} className="editor-input" placeholder="my-article-slug" data-testid="input-slug" />
            </Field>
            <Field label="Excerpt (2–3 lines)" testid="field-excerpt">
              <textarea value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={3} className="editor-input" data-testid="input-excerpt" />
            </Field>
            <Field label="Body (Markdown — use ## for headings)" testid="field-body">
              <textarea ref={bodyRef} value={form.body} onChange={(e) => update("body", e.target.value)} rows={22} className="editor-input font-mono" data-testid="input-body" style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: "0.9rem" }} />
              <div className="mt-3 flex flex-wrap gap-4 items-center">
                <label className="btn-outline cursor-pointer inline-block" data-testid="attach-pdf-button">
                  <input type="file" accept="application/pdf" onChange={handlePdfAttach} style={{ display: "none" }} data-testid="attach-pdf-input" />
                  {pdfUploading ? "Uploading PDF…" : "Attach PDF to body"}
                </label>
                <label className="btn-outline cursor-pointer inline-block" data-testid="attach-image-button">
                  <input type="file" accept="image/*" onChange={handleInlineImage} style={{ display: "none" }} data-testid="attach-image-input" />
                  {inlineImgUploading ? "Uploading image…" : "Attach image to body"}
                </label>
                <span className="text-xs opacity-70">Files upload and insert a link/image at your cursor position.</span>
              </div>
              <div className="mt-3 p-4 text-xs" style={{ background: "var(--sand)", lineHeight: 1.7 }}>
                <div className="overline mb-2">Formatting cheat sheet</div>
                <div><code>## Section heading</code> — larger section title</div>
                <div><code>**bold**</code> / <code>*italic*</code> — emphasis</div>
                <div><code>&gt; quote</code> — pull-quote / callout</div>
                <div className="mt-2"><strong>Bulleted list</strong> (add a blank line above):</div>
                <div style={{ fontFamily: "monospace", whiteSpace: "pre" }}>{`
- First point
- Second point
- Third point`}</div>
                <div className="mt-2"><strong>Numbered list</strong>:</div>
                <div style={{ fontFamily: "monospace", whiteSpace: "pre" }}>{`
1. First step
2. Second step
3. Third step`}</div>
                <div className="mt-2"><strong>Links</strong>:</div>
                <div><code>[Link text](https://example.com)</code> — external (opens in new tab)</div>
                <div><code>[Text](/resources/sibo-starter-guide)</code> — internal (same tab)</div>
                <div><code>[Read more on symptoms](/topic/topic-slug)</code> — link to a topic page</div>
                <div className="mt-2"><strong>Inline image</strong>: <code>![alt text](https://…image.jpg)</code></div>
                <div className="opacity-70">Tip: use the buttons below the body to upload a PDF or image and auto-insert it at your cursor.</div>
              </div>
            </Field>
          </div>
          <div className="md:col-span-1 space-y-5">
            <Field label="Category" testid="field-category">
              <select value={form.category} onChange={(e) => update("category", e.target.value)} className="editor-input" data-testid="input-category">
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Kicker (small label)" testid="field-kicker">
              <input type="text" value={form.kicker} onChange={(e) => update("kicker", e.target.value)} className="editor-input" data-testid="input-kicker" />
            </Field>
            <Field label="Featured image" testid="field-image">
              <div className="flex flex-col gap-3">
                <label className="btn-outline cursor-pointer inline-block w-fit">
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} data-testid="input-image-upload" />
                  {imgUploading ? "Uploading…" : (form.image ? "Replace image" : "Upload image")}
                </label>
                <div className="text-xs opacity-70">Or paste a direct image URL (must end in .jpg / .png / .webp — not a webpage URL).</div>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => { update("image", e.target.value); setImgError(false); }}
                  className="editor-input"
                  placeholder="https://…image.jpg"
                  data-testid="input-image"
                />
                {form.image && form.image.trim() ? (
                  imgError ? (
                    <div className="p-4 border text-sm" style={{ borderColor: "#c2996b", background: "#f4ecdf", color: "#7a4f1f" }} data-testid="image-error">
                      Image failed to load. This URL is likely a webpage, not the image file. Try the <strong>Upload image</strong> button instead, or right-click the image on its source page → <strong>Copy image address</strong>.
                    </div>
                  ) : (
                    <img
                      src={form.image}
                      alt="preview"
                      className="mt-2 w-full aspect-[4/3] object-cover"
                      onError={() => setImgError(true)}
                      onLoad={() => setImgError(false)}
                      data-testid="image-preview"
                    />
                  )
                ) : null}
              </div>
            </Field>
            <Field label="Author" testid="field-author">
              <input type="text" value={form.author} onChange={(e) => update("author", e.target.value)} className="editor-input" data-testid="input-author" />
            </Field>
            <Field label="Reading time" testid="field-reading-time">
              <input type="text" value={form.reading_time} onChange={(e) => update("reading_time", e.target.value)} className="editor-input" data-testid="input-reading-time" />
            </Field>
            <Field label="Date label" testid="field-date">
              <input type="text" value={form.date} onChange={(e) => update("date", e.target.value)} className="editor-input" placeholder="Feb 2026" data-testid="input-date" />
            </Field>
            <label className="flex items-center gap-3 text-sm" data-testid="field-published">
              <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} data-testid="input-published" />
              Published (visible on site)
            </label>
          </div>
        </div>
      )}
      <style>{`
        .editor-input {
          width: 100%; padding: 0.65rem 0.9rem;
          border: 1px solid rgba(79,87,70,0.35);
          background: transparent; color: var(--olive);
          font-size: 0.95rem; outline: none;
        }
        .editor-input:focus { border-color: var(--olive); }
      `}</style>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}
